import React, { useEffect, useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";
import {
  DataGrid,
  gridClasses,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";

const LogisticsDraftTable = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};

  const fetchDrafts = async () => {
    if (!storedUser.username) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/logistic-draft?username=${storedUser.username}`,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      // Show only true drafts. Lifecycle statuses must never appear in Drafts tab.
      const lifecycleStatuses = new Set([
        "ACCEPTING_BIDS",
        "BIDS_CLOSED",
        "REBIDDING",
        "EAUCTIONED",
        "SUBMITTED",
        "AWARDED",
      ]);
      const draftOnly = (res.data || []).filter(
        (r) => !lifecycleStatuses.has((r.status || "").toUpperCase())
      );
      setDrafts(draftOnly);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDrafts(); }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this draft?")) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/logistic-draft?logisticDraftId=${id}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
        );
        fetchDrafts();
      } catch (error) {
        console.error("Delete error:", error);
      }
    }
  };

  const columns = [
    {
      field: "logisticDraftId",
      headerName: "Logistics RFQ ID",
      flex: 1,
      renderCell: (params) => (
        <span
          className="table-title"
          style={{ fontWeight: "bold", cursor: "pointer" }}
          onClick={() => {
            localStorage.setItem("logisticActiveTab", "DRAFTS");
            navigate(`/logistic-dashboard/logistic-creation/draft/${params.value}`);
          }}
        >
          {params.value}
        </span>
      ),
    },
    { field: "modeOfConsignment", headerName: "Mode", flex: 1 },
    { field: "consignee", headerName: "Consignee", flex: 1 },
    { field: "origin", headerName: "Origin", flex: 1 },
    { field: "destination", headerName: "Destination", flex: 1 },
    // { field: "volumeCBM", headerName: "Volume (CBM)", flex: 1 },
    {
      field: "submissionDate",
      headerName: "Submission Date",
      flex: 1,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            {params.value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Button
          sx={{ padding: "0 !important", minWidth: "0px !important" }}
          onClick={() => handleDelete(params.row.logisticDraftId)}
        >
          <DeleteIcon fontSize="small" />
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", minHeight: "400px" }}>
      <DataGrid
        rows={drafts}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.logisticDraftId}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        pageSizeOptions={[10]}
        disableRowSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        disableColumnMenu
        disableColumnResize
        slots={{ toolbar: CustomToolbar }}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
        sx={{
          border: "none",
          fontFamily: "Inter !important",
          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "700 !important" },
          "& .MuiDataGrid-columnHeaders": {
            background: "none !important",
            color: "black",
            fontSize: "14px",
          },
          "& .MuiDataGrid-row:hover": { backgroundColor: "#fff" },
          "& .MuiDataGrid-cell": { fontSize: "13px", border: "none" },
          "& .MuiDataGrid-columnSeparator": { display: "none" },
          [`& .${gridClasses.row}.even`]: { backgroundColor: "#EAF4FF" },
          "& .MuiInput-underline:before": { borderBottom: "none !important" },
        }}
      />
    </Box>
  );
};

export default LogisticsDraftTable;

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ display: "flex", justifyContent: "end" }}>
      <GridToolbarQuickFilter
        sx={{
          border: "1px solid #B0B0B0",
          borderRadius: "12px",
          padding: "4px 12px",
          "&:hover": { backgroundColor: "#D8E9F0" },
          "& .MuiInputBase-root": { borderRadius: "12px" },
          width: 300,
        }}
      />
    </GridToolbarContainer>
  );
}
